import { useLanguage } from '../context/LanguageContext';
import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Calendar, Clock, MapPin, Ticket, ExternalLink, Play } from 'lucide-react';
import { EVENTS } from '../data/events';
import { ShareButtons } from '../components/ShareButtons';
import { PhotoLightbox } from '../components/PhotoLightbox';

const EventCard = ({ event, language, isPast, onPhotoClick }) => {
  const lang = language === 'en' ? 'en' : 'fr';
  const dateLabel = new Date(event.start).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div
      data-testid={`event-card-${event.id}`}
      className="bg-white rounded-3xl shadow-sm overflow-hidden border border-[#E2E8F0]"
    >
      <div className="grid lg:grid-cols-5">
        <div className="lg:col-span-2">
          <img
            src={event.image}
            alt={event.title[lang]}
            loading="lazy"
            className="w-full h-full max-h-[560px] object-cover"
          />
        </div>
        <div className="lg:col-span-3 p-8 sm:p-10 flex flex-col">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4AF37]/15 text-[#9A7B1E] text-sm font-medium">
              <Calendar className="w-4 h-4" />
              {dateLabel}
            </span>
            {isPast && (
              <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-500 text-xs">
                {lang === 'fr' ? 'Événement passé' : 'Past event'}
              </span>
            )}
          </div>

          <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#1A252C] mb-4">
            {event.title[lang]}
          </h3>
          <p className="text-[#546A76] leading-relaxed mb-6">
            {event.description[lang]}
          </p>

          <div className="flex flex-wrap gap-2 mb-6">
            {(event.highlights ? event.highlights[lang] : []).map((h) => (
              <span key={h} className="px-3 py-1 rounded-full bg-[#4A708B]/10 text-[#4A708B] text-sm font-medium">
                {h}
              </span>
            ))}
          </div>

          <div className="space-y-3 mb-8">
            {event.timeLabel && (
              <div className="flex items-center gap-3 text-[#1A252C]">
                <Clock className="w-5 h-5 text-[#4A708B] flex-shrink-0" />
                <span>{event.timeLabel[lang]}</span>
              </div>
            )}
            {event.location && (
              <div className="flex items-center gap-3 text-[#1A252C]">
                <MapPin className="w-5 h-5 text-[#4A708B] flex-shrink-0" />
                <span>{event.location}</span>
              </div>
            )}
            {event.price && (
              <div className="flex items-center gap-3 text-[#1A252C]">
                <Ticket className="w-5 h-5 text-[#4A708B] flex-shrink-0" />
                <span className="font-semibold">{event.price[lang]}</span>
              </div>
            )}
          </div>

          {event.photos && event.photos.length > 0 && (
            <div className="mb-8" data-testid={`event-gallery-${event.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <p className="text-sm font-medium text-[#546A76]">
                  {lang === 'fr' ? "Photos de l'événement" : 'Event photos'}
                </p>
                {event.photos.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onPhotoClick(event, 0, true)}
                    data-testid={`event-slideshow-btn-${event.id}`}
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4AF37]/15 text-[#9A7B1E] text-sm font-medium hover:bg-[#D4AF37]/30 transition-colors"
                  >
                    <Play className="w-4 h-4" />
                    {lang === 'fr' ? 'Lancer le diaporama' : 'Start slideshow'}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {event.photos.map((photo, i) => (
                  <button
                    key={photo}
                    type="button"
                    onClick={() => onPhotoClick(event, i)}
                    aria-label={`${lang === 'fr' ? 'Agrandir la photo' : 'Enlarge photo'} ${i + 1}`}
                    data-testid={`event-photo-${event.id}-${i}`}
                    className="cursor-zoom-in"
                  >
                    <img
                      src={photo}
                      alt={`${event.title[lang]} - photo ${i + 1}`}
                      loading="lazy"
                      className="rounded-2xl object-cover object-top w-full h-44 sm:h-52 hover:opacity-90 transition-opacity"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {!isPast && (
            <div className="mt-auto space-y-6">
              <a
                href={event.ticketUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`event-tickets-btn-${event.id}`}
              >
                <Button className="gold-btn rounded-full px-8 py-6 text-base">
                  {lang === 'fr' ? 'Acheter mes billets sur Eventbrite' : 'Buy my tickets on Eventbrite'}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <ShareButtons
                url="https://fondationenat.org/events"
                title={`${event.title[lang]} — ${lang === 'fr' ? '30 août 2026, Montréal' : 'August 30, 2026, Montreal'}`}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function EventsPage() {
  const { language } = useLanguage();
  const lang = language === 'en' ? 'en' : 'fr';
  const [lightbox, setLightbox] = useState(null);
  const now = new Date();
  const upcoming = EVENTS.filter((e) => new Date(e.end) >= now)
    .sort((a, b) => new Date(a.start) - new Date(b.start));
  const past = EVENTS.filter((e) => new Date(e.end) < now)
    .sort((a, b) => new Date(b.start) - new Date(a.start));

  return (
    <div data-testid="events-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#D4AF37] font-medium tracking-widest uppercase text-sm mb-4">
            {lang === 'fr' ? 'Événements' : 'Events'}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A252C] mb-6">
            {lang === 'fr' ? 'Nos événements' : 'Our Events'}
          </h1>
          <p className="text-[#546A76] text-lg max-w-2xl">
            {lang === 'fr'
              ? "Participez à nos événements-bénéfice et contribuez directement à soutenir les femmes et enfants victimes de violence au Québec."
              : 'Join our fundraising events and directly help support women and children victims of violence in Quebec.'}
          </p>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-20 bg-white" data-testid="upcoming-events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl font-bold text-[#1A252C] mb-10">
            {lang === 'fr' ? 'À venir' : 'Upcoming'}
          </h2>
          {upcoming.length > 0 ? (
            <div className="space-y-10">
              {upcoming.map((event) => (
                <EventCard key={event.id} event={event} language={language} isPast={false} onPhotoClick={(ev, i, auto) => setLightbox({ event: ev, index: i, autoPlay: !!auto })} />
              ))}
            </div>
          ) : (
            <p className="text-[#546A76]" data-testid="no-upcoming-events">
              {lang === 'fr'
                ? 'Aucun événement à venir pour le moment. Revenez bientôt !'
                : 'No upcoming events at the moment. Check back soon!'}
            </p>
          )}
        </div>
      </section>

      {/* Past Events */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="past-events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl font-bold text-[#1A252C] mb-10">
            {lang === 'fr' ? 'Événements passés' : 'Past Events'}
          </h2>
          {past.length > 0 ? (
            <div className="space-y-10">
              {past.map((event) => (
                <EventCard key={event.id} event={event} language={language} isPast={true} onPhotoClick={(ev, i, auto) => setLightbox({ event: ev, index: i, autoPlay: !!auto })} />
              ))}
            </div>
          ) : (
            <p className="text-[#546A76]" data-testid="no-past-events">
              {lang === 'fr'
                ? 'Nos événements passés apparaîtront ici.'
                : 'Our past events will appear here.'}
            </p>
          )}
        </div>
      </section>

      {lightbox && (
        <PhotoLightbox
          key={lightbox.autoPlay ? 'auto' : 'manual'}
          photos={lightbox.event.photos}
          index={lightbox.index}
          title={lightbox.event.title[lang]}
          autoPlay={lightbox.autoPlay}
          onClose={() => setLightbox(null)}
          onNavigate={(i) => setLightbox((lb) => ({ ...lb, index: i }))}
        />
      )}
    </div>
  );
}
